import React, { useState, useMemo } from 'react';

const PaginatedTable = ({ columns, data, rowsPerPage = 10, onRowClick }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const totalEntries = data.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return [...data];
        const sorted = [...data].sort((a, b) => {
            const aVal = a[sortConfig.key] ?? '';
            const bVal = b[sortConfig.key] ?? '';
            return aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
        });
        return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
    }, [data, sortConfig]);

    const paginatedData = sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '▲▼';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const renderPageButtons = () => {
        const pages = [];

        const prev = Math.max(1, currentPage - 1);
        const next = Math.min(totalPages, currentPage + 1);

        const uniquePages = Array.from(new Set([prev, currentPage, next]));

        uniquePages.forEach(page => {
            pages.push(
                <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={page === currentPage ? 'active' : ''}
                >
                    {page}
                </button>
            );
        });

        return pages;
    };

    const from = (currentPage - 1) * rowsPerPage + 1;
    const to = Math.min(currentPage * rowsPerPage, totalEntries);

    return (
        <div className="paginated-table-container">
            <table className="paginated-table">
                <thead>
                    <tr>
                        {columns.map(col => (
                            <th key={col.accessor} onClick={() => handleSort(col.accessor)}>
                                {col.label}{getSortIndicator(col.accessor)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((row, idx) => (
                        <tr key={idx} onClick={() => onRowClick?.(row)} className={onRowClick ? 'clickable-row' : ''}>
                            {columns.map(col => (
                                <td key={col.accessor} className="hoverable-cell">
                                    {col.hover && row[col.hover] ? (
                                        <>
                                            <span className="hover-text">{row[col.accessor]}</span>
                                            <div className="custom-tooltip">{row[col.hover]}</div>
                                        </>
                                    ) : (
                                        row[col.accessor]
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>

            </table>

            <div className="pagination-bar">
                <div className="pagination-summary">Showing {from}–{to} of {totalEntries} entries</div>
                <div className="pagination-controls">
                    <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>

                    {/* Page buttons */}
                    {renderPageButtons()}

                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                    <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
                </div>
            </div>
        </div>
    );
};

export default PaginatedTable;
